import { ColorKey } from '../../../utilis/types/Types';
import {User} from "../Users/userService";
import {Group} from "../groups/groupsService";
import {api} from "../../api.ts";
import {unwrapData} from "../../apiResponse.ts";
import {showToast} from '../Notifications/notificationsSlice';


export interface ContactSelectItem {
    contactId: number;
    firstName: string;
    lastName: string;
    phoneNumbers: ContactListPhoneNumber[];
    sharedFor: SharedItem[];
    iconColor?: ColorKey;
    sharedForUsers: User[];
    sharedForGroups: Group[];
    availability: string;
    owner?: ContactOwner | null;
}

export interface ContactList {
    contacts: ContactSelectItem[];
    totalCount: number;
    sourceCounts: Record<string, number>;
}

export interface ExternalBulkContactInput {
    ownerId: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    company?: string;
    availability: 'PRIVATE' | 'PUBLIC' | 'SHARED';
    phoneNumbers: { phoneNumber: string; main: boolean }[];
    externalIdentifier: { id: string; name: string };
    source?: string;
    sharedForUsers?: string[];
    sharedForGroups?: string[];
}

export interface ExternalBulkContactError {
    externalIdentifier: { id: string; name: string };
    errors: {
        field?: string;
        issues?: { message: string }[];
    }[];
}

export type SharedItem = {
    userId?: number;
    groupId?: number;
    iconColor: ColorKey;
    name: string;
    fullName?: string;
};

export interface Contact {
    contactId: number;
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    availability: string;
    createdBy: string;
    phoneNumbers: { msisdn: number; main: boolean }[];
    sharedForUsers: User[];
    sharedForGroups: Group[];
    createdAt: string;
    iconColor?: ColorKey;
    contactType: ContactType;
    url?: string | null;
    owner?: ContactOwner | null;
}

export interface ContactOwner {
    userId: number;
    firstName?: string | null;
    lastName?: string | null;
    iconColor?: ColorKey;
}

export interface ContactDTO {
    contactId?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    company?: string;
    availability: 'PRIVATE' | 'PUBLIC' | 'SHARED';
    phoneNumbers: { msisdn: number; main: boolean }[];
    sharedForUsers: number[];
    sharedForGroups: number[];
    iconColor?: ColorKey;
}


interface ParamsProps {
    phrase?: string;
    order?: string;
    sortBy?: string;
    sharedForUsers?: number[];
    sharedForGroups?: number[];
    lastContactId?: number;
    lastLastName?: string
    lastFirstName?: string;
    lastMsisdn?: number;
    firstName?: string;
    userId?: string;
    limit: number;
    offset: number;
    sources?: string[];
    dateFrom?: string;
    dateTo?: string;
}
export interface ContactType {
    type: 'INTERNAL' | 'EXTERNAL';
    source?: string;
}

export interface ContactListItem {
    contactId: number;
    firstName: string;
    lastName: string;
    phoneNumbers: ContactListPhoneNumber[];
    sharedForUsers: User[];
    sharedForGroups:Group[];
    availability: string;
    contactType?: ContactType;
    owner?: ContactOwner | null;
}

export interface ContactListPhoneNumber {
    phoneNumber: number;
    main: boolean;
}

export type ContactIdentity = {
    contactId: number;
    firstName: string;
    lastName: string;
    phoneNumber: number;
};

type ContactResponse = {
    primary: ContactIdentity;
    duplicated: ContactIdentity[];
}[];
export const contactsApi= api.injectEndpoints({
    endpoints: (builder) => ({
        getContacts: builder.infiniteQuery<
            ContactList,
            ContactList,
            ParamsProps
        >({
            infiniteQueryOptions: {
                initialPageParam: {

                    offset: 0,
                    limit: 20,
                },
                getNextPageParam: (
                    lastPage,
                    allPages,
                    lastPageParam,
                ) => {
                    if (!lastPage.contacts.length ||
                        allPages.reduce((total, page) => total + page.contacts.length, 0) >= lastPage.totalCount) {
                        return undefined
                    }
                    const nextOffset = lastPageParam.offset + lastPageParam.limit
                    return {
                        offset: nextOffset,
                        limit: lastPageParam.limit,
                    }
                },
            },
            query: ({queryArg, pageParam}) => {

                return ({
                    url: `/api/v1/contacts`,
                    params: {
                        ...queryArg,
                        offset: pageParam?.offset || 0,
                        limit: pageParam?.limit || 20,
                    },
                })
            },
            transformResponse: (response: {data: ContactSelectItem[]; sourceCounts?: Record<string, number>}, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count')
                return {
                    contacts: unwrapData(response) || [],
                    totalCount: totalCount ? parseInt(totalCount, 10) : 0,
                    sourceCounts: response?.sourceCounts ?? {},
                }
            },
            providesTags: [{type: 'Contact', id: 'LIST'}],
        }),
        getContactById: builder.query<Contact, number>({
            query: (contactId) => `/contact/${contactId}`,
            transformResponse: (response: Contact | { data: Contact }) => unwrapData(response),
            providesTags: (contact) => contact ? [{ type: 'Contact', id: contact.contactId }] : [],
        }),
        addExternalContactsBulk: builder.mutation<void, ExternalBulkContactInput[]>({
            query: (contacts) => ({
                url: '/api/v1/contacts/external-bulk',
                method: 'POST',
                body: contacts,
            }),
            invalidatesTags: [{type: 'Contact', id: 'LIST'}],
        }),
        addContact: builder.mutation<void, ContactDTO>({
            query: (newContact) => ({
                url: '/contact',
                method: 'POST',
                body: newContact,
            }),
            transformResponse: (response: void | { data: void }) => unwrapData(response),
            invalidatesTags: ['Contact'],

            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Contact added successfully!', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to add contact!', type: 'error'}));
                }
            },

        }),
        updateContact: builder.mutation<ContactDTO, ContactDTO>({
            query: (contactData) => ({
                url: `/contact/${contactData.contactId}`,
                method: 'PATCH',
                body: contactData,
            }),
            transformResponse: (response: ContactDTO | { data: ContactDTO }) => unwrapData(response),
            invalidatesTags: (contactData) => [{ type: 'Contact', id: contactData?.contactId }, { type: 'Contact', id: 'LIST' }],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Contact updated successfully!', type: 'success'}));
                } catch (error) {
                    const err = error as { error?: { data?: { message?: string } } };
                    const message = err?.error?.data?.message ?? 'Failed to update contact!';
                    dispatch(showToast({description: message, type: 'error'}));
                }
            },
        }),
        bulkUpdateContactAccess: builder.mutation<void, {
            contactIds: number[];
            availability: 'PRIVATE' | 'PUBLIC' | 'SHARED';
            sharedForUsers?: number[];
            sharedForGroups?: number[];
        }>({
            query: (body) => ({
                url: '/api/v1/contacts/access',
                method: 'PUT',
                body,
            }),
            invalidatesTags: [{type: 'Contact', id: 'LIST'}],
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    const count = arg.contactIds.length;
                    dispatch(showToast({description: `Access updated for ${count} contact${count === 1 ? '' : 's'}.`, type: 'success'}));
                } catch (error) {
                    // Surface the backend reason (e.g. external/SIM contacts can't be edited).
                    const err = error as { error?: { data?: { message?: string } } };
                    const message = err?.error?.data?.message ?? 'Failed to update access.';
                    dispatch(showToast({description: message, type: 'error'}));
                }
            },
        }),
        deleteContactById: builder.mutation<Contact, number>({
            query: (contactId) => ({
                url: `/contact/${contactId}`,
                method: 'DELETE',
            }),
            transformResponse: (response: Contact | { data: Contact }) => unwrapData(response),
            invalidatesTags: [{type: 'Contact', id: 'LIST'}],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Contact deleted successfully!', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to delete contact!', type: 'error'}));
                }
            },
        }),
        getContactsPaginated: builder.query<ContactList, Omit<ParamsProps, 'lastContactId' | 'lastLastName' | 'lastFirstName' | 'lastMsisdn'>>({
            query: (params) => ({
                url: `/api/v1/contacts`,
                params: {
                    ...params,
                    offset: params.offset ?? 0,
                    limit: params.limit ?? 20,
                },
            }),
            transformResponse: (response: { data: ContactSelectItem[]; sourceCounts?: Record<string, number> }, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count');
                return {
                    contacts: unwrapData(response) || [],
                    totalCount: totalCount ? parseInt(totalCount, 10) : 0,
                    sourceCounts: response?.sourceCounts ?? {},
                };
            },
            providesTags: [{ type: 'Contact', id: 'LIST' }],
        }),
        searchContactSuggestions: builder.query<ContactResponse, {phrase: string}>({
            query: ({phrase}) => ({
                url: `/api/v1/contacts/suggestions`,
                params: {phrase, exactMatch: false},
            }),
            transformResponse: (response: { data: ContactResponse }) => unwrapData(response),
        }),
        getContactByPhoneNumber: builder.query<ContactResponse, {msisdn: string, userId: string | null}>({
            query: ({msisdn}) => ({
                url: `/api/v1/contacts/suggestions`,
                params: {phrase: msisdn, exactMatch: true},
            }),
            transformResponse: (response: { data: ContactResponse }) => unwrapData(response),
        }),
    }),
});


export const {
    useGetContactsInfiniteQuery,
    useGetContactsPaginatedQuery,
    useGetContactByIdQuery,
    useAddContactMutation,
    useAddExternalContactsBulkMutation,
    useUpdateContactMutation,
    useBulkUpdateContactAccessMutation,
    useDeleteContactByIdMutation,
    useGetContactByPhoneNumberQuery,
    useSearchContactSuggestionsQuery,
    useLazyGetContactByPhoneNumberQuery,
} = contactsApi;
